/**
 * §58 PR 2 — queries and mutations for a hauler's Plan & Features tab.
 * Every mutation answers with the fresh plan, which replaces the cached one.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { DirectTemplate, FeatureId, HaulerPlanView } from '@lodex-pro/shared';

export const HAULER_PLAN_KEY = (tenantId: string): readonly unknown[] => [
  'platform',
  'hauler-plan',
  tenantId,
];

interface PlanResponse {
  plan: HaulerPlanView;
}

export function useHaulerPlan(tenantId: string): ReturnType<typeof useApiQuery<PlanResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PlanResponse>(opts, `/v1/platform/haulers/${tenantId}/plan`, {
    queryKey: HAULER_PLAN_KEY(tenantId),
    staleTime: 15_000,
  });
}

/** Writes the response's plan into the cache, so the tab shows it without a refetch. */
function useStorePlan(tenantId: string): (res: PlanResponse) => void {
  const qc = useQueryClient();
  return (res) => {
    qc.setQueryData(HAULER_PLAN_KEY(tenantId), res);
  };
}

export interface ApplyTemplateBody {
  template: DirectTemplate;
  reason: string;
  clearOverrides: boolean;
}

export function useApplyTemplate(tenantId: string): {
  mutation: ReturnType<typeof useApiMutation<ApplyTemplateBody, PlanResponse>>;
  store: (res: PlanResponse) => void;
} {
  const opts = useApiOpts();
  const store = useStorePlan(tenantId);
  const mutation = useApiMutation<ApplyTemplateBody, PlanResponse>(
    opts,
    `/v1/platform/haulers/${tenantId}/template`,
    'PUT',
  );
  return { mutation, store };
}

export interface SetOverrideBody {
  enabled: boolean;
  limit: number | null;
  expiresAt: string | null;
  reason: string;
}

export function useSetFeatureOverride(
  tenantId: string,
  featureId: FeatureId,
): {
  mutation: ReturnType<typeof useApiMutation<SetOverrideBody, PlanResponse>>;
  store: (res: PlanResponse) => void;
} {
  const opts = useApiOpts();
  const store = useStorePlan(tenantId);
  const mutation = useApiMutation<SetOverrideBody, PlanResponse>(
    opts,
    `/v1/platform/haulers/${tenantId}/features/${featureId}`,
    'PUT',
  );
  return { mutation, store };
}

export function useResetFeature(
  tenantId: string,
  featureId: FeatureId,
): {
  mutation: ReturnType<typeof useApiMutation<{ reason: string }, PlanResponse>>;
  store: (res: PlanResponse) => void;
} {
  const opts = useApiOpts();
  const store = useStorePlan(tenantId);
  const mutation = useApiMutation<{ reason: string }, PlanResponse>(
    opts,
    `/v1/platform/haulers/${tenantId}/features/${featureId}/reset`,
    'POST',
  );
  return { mutation, store };
}
